import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../../auth/store";
import { ErrorState } from "../../../components/States";
import { AutoLoadMore, PageHeader } from "../../../components/ui";
import { backendApi, backendKeys, type TraceQuery, type TraceSummary } from "../api";
import { BackendActions, BackendTabs, ConnectPrompt, DebouncedSearch, ServiceSelect, SessionTable, StatusBadge, blocksBySession, ms, replayLink, useBackendRange, useFacets, useFilters, when } from "../components";

const KEYS = ["q", "service", "status", "linked", "route"] as const;

export function TracesPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [range] = useBackendRange();
  const [f, setF, clear] = useFilters(KEYS);
  const facets = useFacets(range);
  const query: TraceQuery = { range, q: f.q, service: f.service, route: f.route, status: f.status as TraceQuery["status"], linked: f.linked === "1" };
  const q = useInfiniteQuery({
    queryKey: backendKeys.traces(auth.currentProjectId, query),
    queryFn: ({ pageParam }) => backendApi.traces({ ...query, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!auth.currentProjectId,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const blocks = useMemo(() => blocksBySession(rows, (t) => t.startMs), [rows]);
  const filtered = Object.keys(f).length > 0;
  const nothingYet = !q.isPending && !filtered && rows.length === 0 && facets.isSuccess && facets.data.services.length === 0;

  return (
    <>
      <PageHeader title="Backend" actions={<BackendActions />}>
        <BackendTabs />
        <div className="ins-filters" role="search" aria-label="Request filters">
          <DebouncedSearch value={f.q} onChange={(q) => setF({ q })} placeholder="Request, error, trace or session ID…" label="Search requests" />
          <ServiceSelect value={f.service} onChange={(service) => setF({ service })} services={facets.data?.services ?? []} />
          <label>
            Status
            <select value={f.status ?? ""} onChange={(e) => setF({ status: e.target.value || undefined })} autoComplete="off">
              <option value="">Any status</option>
              <option value="error">Errors</option>
              <option value="slow">Slow (over 1 s)</option>
            </select>
          </label>
          <label>
            Replay
            <select value={f.linked ?? ""} onChange={(e) => setF({ linked: e.target.value || undefined })} autoComplete="off">
              <option value="">Any request</option>
              <option value="1">Has a replay</option>
            </select>
          </label>
          {filtered && <button type="button" className="btn ghost" onClick={clear}>Clear</button>}
        </div>
      </PageHeader>

      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : nothingYet ? (
        <ConnectPrompt title="No backend requests yet" hint="Add the Backstory package to a service and its requests show up here, linked to the replays that made them." />
      ) : (
        <div className="card table-scroll stack-table be-table">
          <SessionTable<TraceSummary>
            ariaLabel="Backend requests"
            blocks={blocks}
            noun="request"
            rowKey={(t) => t.spanId}
            loading={q.isPending}
            loadingMore={q.isFetchingNextPage}
            onRowClick={(t) => void navigate({ to: "/backend/traces/$traceId" as never, params: { traceId: t.traceId } as never })}
            empty={filtered ? <>No requests match these filters. <button type="button" className="btn ghost small" onClick={clear}>Clear filters</button></> : "No requests in this time range."}
            columns={[
              {
                key: "req",
                header: "Request",
                render: (t) => (
                  <span className="cell-stack">
                    <strong className="ins-title">{t.name}</strong>
                    <small>
                      {t.service}
                      {t.exceptionType ? <> · <span className="be-exception">{t.exceptionType}{t.exceptionMessage ? `: ${t.exceptionMessage}` : ""}</span></> : null}
                    </small>
                  </span>
                ),
              },
              { key: "status", header: "Status", label: "Status", width: 90, render: (t) => <StatusBadge status={t.status} httpStatus={t.httpStatus} /> },
              { key: "dur", header: "Duration", label: "Duration", width: 110, align: "right", render: (t) => <span className="mono">{ms(t.durationMs)}</span> },
              { key: "at", header: "Started", label: "Started", width: 170, render: (t) => <span className="small">{when(t.startMs)}</span> },
              {
                key: "replay",
                header: "Replay",
                label: "Replay",
                width: 110,
                render: (t) =>
                  t.sessionId ? (
                    <Link {...replayLink(t.sessionId, t.startMs)} className="be-replay" onClick={(e) => e.stopPropagation()}>
                      Watch replay
                    </Link>
                  ) : (
                    <span className="muted small">None</span>
                  ),
              },
            ]}
          />
          <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more requests" />
        </div>
      )}
    </>
  );
}
